import React, { useState } from 'react';
import CopySvg from 'images/copy.svg';
import { formatCurrency } from 'Util/_HumanWritten/CurrencyUtils';
import alertToast from 'Util/ToastifyUtils';
import Icon from '../Icon/Icon';

export interface VoucherTicketProps {
	/** Whether this is a gift voucher (vs credit) */
	isGift: boolean;
	/** The voucher code */
	code: string;
	/** The current remaining value */
	amount: number;
	/** The original value (if different from amount, shows "remaining / total") */
	initialAmount?: number;
	/** Status: 'active', 'redeemed', 'expired', or 'voided' */
	status: 'active' | 'redeemed' | 'expired' | 'voided' | 'refunded';
	/** Expiry text to display (e.g. "Expires 01 Jan 2027") */
	expiryText: string;
	/** Whether a message exists on this voucher */
	hasMessage?: boolean;
	/** The full message text (shown below the ticket when provided) */
	message?: string;
	/** Whether to show the full message below the ticket */
	showMessage?: boolean;
	/** Whether this voucher is inactive (expired/used) */
	inactive?: boolean;
	/** Whether this voucher was just claimed (triggers animation) */
	justClaimed?: boolean;
	/** Whether to show a "New" tag on the corner */
	isNew?: boolean;
	/** Click handler for the whole ticket */
	onClick?: (e: React.MouseEvent) => void;
}

export function VoucherTicket({
	isGift,
	code,
	amount,
	initialAmount,
	status,
	expiryText,
	hasMessage,
	message,
	showMessage,
	justClaimed,
	isNew,
	inactive,
	onClick,
}: VoucherTicketProps) {
	const [copied, setCopied] = useState(false);

	const onClickCode = (e: React.MouseEvent<HTMLElement>) => {
		e.preventDefault();
		e.stopPropagation();
		if (copied) return;
		navigator?.clipboard
			?.writeText(code)
			.then(() => {
				setCopied(true);
				alertToast('Code copied!', 'success');
				setTimeout(() => setCopied(false), 2000);
			})
			.catch(() => {
				alertToast('Failed to copy code', 'error');
			});
	};

	const containerClass = [
		'voucher-ticket',
		isGift ? 'voucher-ticket--gift' : 'voucher-ticket--credit',
		inactive ? 'voucher-ticket--inactive' : '',
		justClaimed ? 'voucher-ticket--just-claimed' : '',
	]
		.filter(Boolean)
		.join(' ');

	const showInitialAmount = initialAmount !== undefined && initialAmount !== amount;

	return (
		<div className="voucher-ticket-wrapper">
			{isNew && <span className="voucher-ticket__new-tag">New</span>}
			<div onClick={onClick} aria-hidden="true" className={containerClass}>
				{/* Left stub */}
				<div className="voucher-ticket__stub">
					<span className="voucher-ticket__stub-label">{isGift ? 'Gift' : 'Credit'}</span>
					<Icon name={isGift ? 'card_giftcard' : 'payments'} useMaterialIcons />
				</div>

				{/* Main body */}
				<div className="voucher-ticket__body">
					<div className="voucher-ticket__watermark">
						<Icon name={isGift ? 'card_giftcard' : 'payments'} useMaterialIcons />
					</div>

					<div className="voucher-ticket__content">
						{/* Top row: type label + amount */}
						<div className="voucher-ticket__top-row">
							<div>
								<p className="voucher-ticket__type-label">
									{isGift ? 'Gift Voucher' : 'Credit Voucher'}
								</p>
								<div className="voucher-ticket__code-row">
									<span className="voucher-ticket__code">{code.toUpperCase()}</span>
									<button
										type="button"
										className={`voucher-ticket__copy-btn ${copied ? 'copied' : ''}`}
										onClick={onClickCode}
									>
										{copied ? (
											<Icon name="check_circle" useMaterialIcons />
										) : (
											<img src={CopySvg} alt="copy" />
										)}
										<span>{copied ? 'Copied' : 'Copy'}</span>
									</button>
								</div>
							</div>
							<div className="voucher-ticket__amount">
								{formatCurrency(amount)}
								{showInitialAmount && (
									<span className="voucher-ticket__initial-amount">
										/ {formatCurrency(initialAmount)}
									</span>
								)}
							</div>
						</div>

						{/* Bottom row: status + expiry + message */}
						<div className="voucher-ticket__bottom-row">
							<div className="voucher-ticket__meta">
								<span className={`voucher-ticket__status voucher-ticket__status--${status}`}>
									{status === 'redeemed' && <Icon name="check_circle" useMaterialIcons />}
									{status === 'expired' && <Icon name="error" useMaterialIcons />}
									{status === 'voided' && <Icon name="block" useMaterialIcons />}
									{status === 'refunded' && <Icon name="block" useMaterialIcons />}
									{
										{
											active: 'Active',
											redeemed: 'Redeemed',
											expired: 'Expired',
											voided: 'Voided',
											refunded: 'Refunded',
										}[status]
									}
								</span>
								<span className="voucher-ticket__expiry">
									<Icon name="schedule" useMaterialIcons />
									{expiryText}
								</span>
							</div>
							{(hasMessage || message) && (
								<div className="voucher-ticket__message-badge">
									<Icon name="mail" useMaterialIcons />
									<span>Message</span>
								</div>
							)}
						</div>
					</div>
				</div>
			</div>
			{showMessage && message && (
				<div className="voucher-ticket__message-full">
					<p className="voucher-ticket__message-label">Message</p>
					<p>{message}</p>
				</div>
			)}
		</div>
	);
}
